/**
 * NotificationItem Component
 * Individual notification with mark-as-read and delete actions
 */

import { Notification } from '../../services/notifications.service'

interface NotificationItemProps {
  notification: Notification
  onMarkAsRead: (id: number) => void
  onDelete: (id: number) => void
}

export default function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
}: NotificationItemProps) {
  const isUnread = notification.is_read === 0
  const createdDate = new Date(notification.created_at)

  // Format relative time
  const getRelativeTime = (date: Date): string => {
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return '剛剛'
    if (diffMins < 60) return `${diffMins} 分鐘前`
    if (diffHours < 24) return `${diffHours} 小時前`
    if (diffDays < 7) return `${diffDays} 天前`
    return date.toLocaleDateString('zh-TW')
  }

  // Get icon and color based on type
  const getTypeIcon = (type: string): string => {
    switch (type) {
      case 'info':
        return 'ℹ️'
      case 'warning':
        return '⚠️'
      case 'error':
        return '❌'
      case 'success':
        return '✅'
      default:
        return 'ℹ️'
    }
  }

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'info':
        return '#3b82f6'
      case 'warning':
        return '#f59e0b'
      case 'error':
        return '#ef4444'
      case 'success':
        return '#10b981'
      default:
        return '#6b7280'
    }
  }

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation()
    onMarkAsRead(notification.id)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete(notification.id)
  }

  return (
    <div
      style={{
        ...styles.container,
        backgroundColor: isUnread ? '#eff6ff' : 'white',
        borderLeft: `4px solid ${getTypeColor(notification.type)}`,
      }}
    >
      {/* Icon and content */}
      <div style={styles.content}>
        <div style={styles.icon}>{getTypeIcon(notification.type)}</div>
        <div style={styles.textContent}>
          <div style={styles.header}>
            <h4 style={styles.title}>{notification.title}</h4>
            <span style={styles.time}>{getRelativeTime(createdDate)}</span>
          </div>
          <p style={styles.message}>{notification.message}</p>
          {notification.category && (
            <span style={styles.category}>{getCategoryLabel(notification.category)}</span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div style={styles.actions}>
        {isUnread && (
          <button
            onClick={handleMarkAsRead}
            style={styles.actionButton}
            title="標記為已讀"
          >
            ✓
          </button>
        )}
        <button onClick={handleDelete} style={styles.actionButton} title="刪除">
          🗑️
        </button>
      </div>
    </div>
  )
}

// Helper function to get category label
function getCategoryLabel(category: string): string {
  switch (category) {
    case 'api_key':
      return 'API Key'
    case 'bot':
      return 'Bot'
    case 'system':
      return '系統'
    case 'report':
      return '報表'
    default:
      return category
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '12px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '8px',
    transition: 'background-color 0.2s',
  },
  content: {
    display: 'flex',
    gap: '12px',
    flex: 1,
  },
  icon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  textContent: {
    flex: 1,
    minWidth: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginBottom: '4px',
  },
  title: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1f2937',
    margin: 0,
  },
  time: {
    fontSize: '12px',
    color: '#6b7280',
    flexShrink: 0,
  },
  message: {
    fontSize: '13px',
    color: '#4b5563',
    margin: '4px 0',
    lineHeight: '1.5',
  },
  category: {
    display: 'inline-block',
    fontSize: '11px',
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    padding: '2px 8px',
    borderRadius: '12px',
    marginTop: '4px',
  },
  actions: {
    display: 'flex',
    gap: '4px',
    flexShrink: 0,
  },
  actionButton: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 8px',
    fontSize: '14px',
    borderRadius: '4px',
    transition: 'background-color 0.2s',
  },
}
